import React, { useState, useEffect, useCallback, useRef } from "react"
import PropTypes from "prop-types"
import { Modal } from "@ciq/shared/components/modal"
import { Button, CloseButton } from "@ciq/shared/components/buttons"
import { PaginatedTable } from "@ciq/shared/components/paginatedTable"
import { FeatureFlags } from "@ciq/shared/constants/featureFlags"
import { useAnalyticsContext } from "@ciq/shared/contexts/AnalyticsContext"
import { useAppContext } from "@ciq/shared/hooks/context"
import { ErrorToast } from "@ciq/shared/utils/toast"
import { DataPreviewTable } from "./DataPreviewTable"
import { useZonePreviewData } from "./useZonePreviewData"
import { useUploadHistoryData } from "./useUploadHistoryData"
import { formatTimestamp, formatNumber } from "@ciq/shared/utils/format"
import { zonesToCSV, generateCSVFilename, downloadCSV } from "./csvUtils"
import { rollbackJob } from "./api"
import ConfirmRollbackModal from "./ConfirmRollbackModal"

/**
 * @typedef {import("./api").HistoryEntry} HistoryEntry
 */

const COLUMNS = [
  { key: "date", label: "Date" },
  { key: "uploaded_by", label: "Uploaded By" },
  { key: "zones", label: "Zones" },
  { key: "rooms", label: "Rooms" },
  { key: "status", label: "Status" },
  { key: "actions", label: "Actions" },
]

/**
 * Status indicator with color-coded icon and label.
 * @param {Object} props
 * @param {string} props.status - Upload status
 * @returns {React.ReactElement}
 */
function StatusIndicator ({ status }) {
  switch (status) {
    case "Applied":
      return (
        <span className="upload-history__status upload-history__status--success">
          <span className="upload-history__status-icon upload-history__status-icon--success">&#10003;</span>
          Applied
        </span>
      )
    case "Cancelled":
      return (
        <span className="upload-history__status upload-history__status--error">
          <span className="upload-history__status-icon upload-history__status-icon--error">&#8212;</span>
          Cancelled
        </span>
      )
    case "Rolling Back":
      return (
        <span className="upload-history__status upload-history__status--applying">
          <span className="upload-history__status-icon upload-history__status-icon--neutral">&#x21BA;</span>
          Rolling Back
        </span>
      )
    case "Rollback Failed":
      return (
        <span className="upload-history__status upload-history__status--error">
          <span className="upload-history__status-icon upload-history__status-icon--error">&#x21BA;</span>
          Rollback Failed
        </span>
      )
    case "Rollback Cancelled":
      return (
        <span className="upload-history__status upload-history__status--error">
          <span className="upload-history__status-icon upload-history__status-icon--error">&#x21BA;</span>
          Rollback Cancelled
        </span>
      )
    default:
      return <span className="upload-history__status">{status}</span>
  }
}

StatusIndicator.propTypes = {
  status: PropTypes.string.isRequired,
}

/**
 * Upload history table showing past zone data uploads for a school.
 *
 * @param {Object} props - Component props
 * @param {string} props.schoolId - School ID to fetch history for
 * @param {string} [props.schoolName] - School name for CSV filename generation
 * @returns {React.ReactElement} The history table
 */
export function UploadHistoryTable ({ schoolId, schoolName, refetchRef, onRollbackComplete }) {
  const [viewModalJob, setViewModalJob] = useState(null)
  const [rollbackTarget, setRollbackTarget] = useState(null)
  const [isRollingBack, setIsRollingBack] = useState(false)

  const { isFeatureEnabled } = useAnalyticsContext()
  const { addLoadingId, removeLoadingId } = useAppContext()
  const showRollback = isFeatureEnabled(FeatureFlags.zone_data_rollback)

  // Reset modal state on school change to prevent stale target IDs
  useEffect(() => {
    setRollbackTarget(null)
    setIsRollingBack(false)
  }, [schoolId])

  // Stable refs to avoid dependency array churn
  const addLoadingIdRef = useRef(addLoadingId)
  const removeLoadingIdRef = useRef(removeLoadingId)
  addLoadingIdRef.current = addLoadingId
  removeLoadingIdRef.current = removeLoadingId

  const historyData = useUploadHistoryData(schoolId)

  // Expose refetch to parent via ref (avoids key-based remount)
  useEffect(() => {
    if (refetchRef) refetchRef.current = historyData.refetch
  }, [refetchRef, historyData.refetch])

  // Manage global loading indicator: initial load + in-progress jobs (rollback/apply)
  useEffect(() => {
    if (!historyData.isLoaded || historyData.hasInProgressJobs) {
      addLoadingIdRef.current("zone_data_history")
    } else {
      removeLoadingIdRef.current("zone_data_history")
    }
  }, [historyData.isLoaded, historyData.hasInProgressJobs])

  // Clean up loading indicator on unmount
  useEffect(() => {
    return () => {
      removeLoadingIdRef.current("zone_data_history")
    }
  }, [])

  // Close modal when school changes to prevent cross-school data leak
  useEffect(() => { setViewModalJob(null) }, [schoolId])

  const zonePreviewData = useZonePreviewData(schoolId, viewModalJob)

  const handleCloseModal = useCallback(() => {
    setViewModalJob(null)
  }, [])

  const handleDownloadCSV = useCallback(() => {
    const filename = generateCSVFilename(schoolName)
    downloadCSV(zonesToCSV(zonePreviewData.allZones), filename)
  }, [zonePreviewData.allZones, schoolName])

  const handleRollbackConfirm = useCallback(async () => {
    if (!rollbackTarget) return
    setIsRollingBack(true)
    try {
      // Find all rollback-eligible jobs newer than the rollback target.
      // Must match backend's rollback_eligible_statuses in apply_position_based_rollback.
      const ROLLBACK_ELIGIBLE = new Set(["Applied", "Rollback Failed", "Rollback Cancelled"])
      const targetIndex = historyData.allJobs.findIndex(j => j.job_id === rollbackTarget.job_id)
      let targets = historyData.allJobs
        .slice(0, targetIndex)  // Jobs newer than target (rows are newest-first)
        .filter(j => ROLLBACK_ELIGIBLE.has(j.status))
        .map(j => j.job_id)

      if (targets.length === 0) {
        // Single Applied row — no prior state to restore to. Backend rejects
        // this as a no-op. The UI should not reach here because can_rollback
        // is False for single Applied rows, but guard defensively.
        ErrorToast("Cannot cancel: no previous zone data state to restore to.")
        return
      }
      // Rollback case — restore to the clicked row
      await rollbackJob(schoolId, targets, rollbackTarget.job_id)
      setRollbackTarget(null)
      historyData.refetch?.()
      onRollbackComplete?.()
    } catch {
      ErrorToast("Failed to initiate rollback. Please try again.")
    } finally {
      setIsRollingBack(false)
    }
  }, [rollbackTarget, schoolId, historyData, onRollbackComplete])

  /**
   * Custom cell renderer for history table rows.
   * @param {HistoryEntry} row - History entry row
   * @param {string} key - Column key
   * @returns {React.ReactNode}
   */
  const renderCell = useCallback((row, key) => {
    switch (key) {
      case "date":
        return formatTimestamp(row.date)
      case "zones":
        return formatNumber(row.zones)
      case "rooms":
        return formatNumber(row.rooms)
      case "status":
        return <StatusIndicator status={row.status} />
      case "actions":
        return (
          <div className="upload-history__actions">
            <button
              type="button"
              className="upload-history__action"
              disabled={!row.can_view}
              onClick={() => setViewModalJob(row.job_id)}
            >
              View
            </button>
            {showRollback && row.can_rollback && (
              <button
                type="button"
                className="upload-history__action upload-history__action--rollback"
                onClick={() => setRollbackTarget(row)}
                aria-label="Rollback"
              >
                Rollback
              </button>
            )}
          </div>
        )
      default:
        return row[key]
    }
  }, [showRollback])

  if (!historyData.isLoaded) {
    return null
  }

  return (
    <div className="upload-history">
      <h5 className="upload-history__title">Upload History</h5>
      <p className="upload-history__subtitle">View and manage previous zone data uploads</p>

      {historyData.error && (
        <p className="text-danger mb-3">{historyData.error}</p>
      )}

      <PaginatedTable
        columns={COLUMNS}
        data={historyData.data}
        totalCount={historyData.totalCount}
        page={historyData.page}
        pageSize={historyData.pageSize}
        onPageChange={historyData.setPage}
        onPageSizeChange={historyData.setPageSize}
        searchValue={historyData.searchQuery}
        onSearch={historyData.setSearchQuery}
        renderCell={renderCell}
        rowKey="job_id"
        emptyMessage="No upload history yet. Past uploads will appear here once files have been processed."
        noResultsMessage="No uploads match your search."
      />

      <Modal show={viewModalJob !== null} onHide={handleCloseModal}>
        <div className="upload-history__modal-header">
          <h4 className="upload-history__modal-title">Upload Details</h4>
          <CloseButton onClick={handleCloseModal} />
        </div>
        <div className="upload-history__modal-body">
          {viewModalJob && (
            <>
              <DataPreviewTable
                data={zonePreviewData.data}
                totalCount={zonePreviewData.totalCount}
                page={zonePreviewData.page}
                pageSize={zonePreviewData.pageSize}
                searchQuery={zonePreviewData.searchQuery}
                isLoading={zonePreviewData.isLoading}
                error={zonePreviewData.error}
                setPage={zonePreviewData.setPage}
                setPageSize={zonePreviewData.setPageSize}
                setSearchQuery={zonePreviewData.setSearchQuery}
                retry={zonePreviewData.retry}
              />
              <div className="upload-history__modal-actions">
                <Button
                  variant="secondary"
                  onClick={handleDownloadCSV}
                  disabled={zonePreviewData.isLoading || zonePreviewData.allZones.length === 0}
                >
                  Download CSV
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>

      <ConfirmRollbackModal
        show={!!rollbackTarget}
        uploadDate={rollbackTarget ? formatTimestamp(rollbackTarget.date) : ""}
        onConfirm={handleRollbackConfirm}
        onCancel={() => setRollbackTarget(null)}
        isSubmitting={isRollingBack}
      />
    </div>
  )
}

UploadHistoryTable.propTypes = {
  schoolId: PropTypes.string.isRequired,
  schoolName: PropTypes.string,
  refetchRef: PropTypes.shape({ current: PropTypes.func }),
  onRollbackComplete: PropTypes.func,
}
